import * as SERVICE from '../../constants/service.types';

import {
  useSocketQuery,
  useSocketRequest,
} from '../../socket/useSocketRequest';
import { useCallback } from 'react';
import {
  parseResponseBody,
  prepareBody,
  requestSocketPromise,
  requestSocketPromiseWithTimeout,
} from '../../socket/query';
import { ROOT_GROUP_ID } from '../../constants/group';
import { beeXLogger } from '../../helpers/logger';

export const listAllDevicesByPage = async (input) => {
  const response = await requestSocketPromiseWithTimeout({
    appServiceType: SERVICE.LIST_ALL_DEVICE_BY_PAGE,
    body: prepareBody(input),
  });

  return parseResponseBody(response);
};

export const useListAllDevicesByPage = () =>
  useSocketRequest({
    appServiceType: SERVICE.LIST_ALL_DEVICE_BY_PAGE,
  });

export const useRemoveGroup = () => {
  const removeGroup = useCallback(async (groupId, namespace) => {
    const subgroupResponse = await requestSocketPromise({
      appServiceType: SERVICE.CHECK_REMOVE_DEVICE_GROUP,
      body: prepareBody({ namespace, deviceGroupId: groupId }),
    });

    const subgroups = parseResponseBody(subgroupResponse);

    await requestSocketPromise({
      appServiceType: SERVICE.DELETE_DEVICE_GROUP,
      body: prepareBody({ namespace, id: groupId }),
    });

    await Promise.all(
      subgroups.map(async (subgroup) => {
        await requestSocketPromise({
          appServiceType: SERVICE.DELETE_DEVICE_GROUP,
          body: prepareBody({ namespace, id: subgroup.id }),
        });
      }),
    );
  }, []);

  return { removeGroup };
};

export const useRemoveCamera = () => {
  const removeCamera = useCallback((serial, groupId, namespace) => {
    beeXLogger.debug('REMOVE_DEVICE_GROUP_MEMBER: ', {
      namespace,
      deviceGroupId: groupId,
      serial,
    });
    return requestSocketPromise({
      appServiceType: SERVICE.REMOVE_DEVICE_GROUP_MEMBER,
      body: prepareBody({
        namespace,
        deviceGroupId: groupId,
        serial,
      }),
    });
  }, []);

  return { removeCamera };
};

export const advanceSearch = async ({
  input,
  timeRequest,
  page,
  size,
  namespace,
  applicationType,
}) => {
  const response = await requestSocketPromiseWithTimeout({
    appServiceType: SERVICE.SEARCH_ADVANCE,
    body: prepareBody({
      applicationType,
      input,
      timeRequest,
      page,
      size,
      namespace,
    }),
  });

  return parseResponseBody(response);
};

/**
 * Warning: AdvanceSearchResult may contain duplicate records
 */
export const useAdvanceSearch = () => {
  const result = useSocketRequest({
    appServiceType: 345,
    timeout: 10000,
  });

  const removeGroup = useCallback(
    (groupId) => {
      result.patch((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          records: {
            ...prev.records,
            TbDeviceGroupMember: prev.records.TbDeviceGroupMember?.filter(
              (item) => item.deviceGroups[groupId] === undefined,
            ),
          },
        };
      });
    },
    [result.patch],
  );

  const removeCamera = useCallback(
    (serial, groupId) => {
      result.patch((prev) => {
        if (!prev) {
          return prev;
        }

        beeXLogger.debug('prev', prev, serial, groupId);

        if (groupId === ROOT_GROUP_ID) {
          return {
            ...prev,
            records: {
              ...prev.records,
              TbDevice: prev.records.TbDevice?.filter(
                (item) => item.serial !== serial,
              ),
            },
          };
        }
        return {
          ...prev,
          records: {
            ...prev.records,
            TbDeviceGroupMember: prev.records.TbDeviceGroupMember?.filter(
              (item) => {
                if (item.serial !== serial) {
                  return true;
                }

                return !item.deviceGroupPath.endsWith(groupId.toString());
              },
            ),
          },
        };
      });
    },
    [result.patch],
  );

  const renameGroup = useCallback(
    (groupId, newName) => {
      result.patch((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          records: {
            ...prev.records,
            TbDeviceGroupMember: prev.records.TbDeviceGroupMember?.map(
              (item) => {
                if (item.deviceGroups[groupId]) {
                  item.deviceGroups[groupId] = {
                    ...item.deviceGroups[groupId],
                    name: newName,
                  };
                }

                return item;
              },
            ),
          },
        };
      });
    },
    [result.patch],
  );

  return {
    ...result,
    removeGroup,
    removeCamera,
    renameGroup,
  };
};

export const useGetDevicesByGroupId = () => {
  return useSocketRequest({
    appServiceType: SERVICE.GET_DEVICE_GROUP_BY_ID,
  });
};

export const useUpdateDevice = () => {
  return useSocketRequest({
    appServiceType: SERVICE.UPDATE_DEVICE_GROUP,
  });
};

export const useAddCameraToGroup = () => {
  return useSocketRequest({
    appServiceType: SERVICE.ADD_DEVICE_GROUP_MEMBER,
  });
};

export const useAddSubgroup = () => {
  return useSocketRequest({
    appServiceType: SERVICE.ADD_DEVICE_GROUP,
  });
};

export const useGetExtraInfo = () => {
  return useSocketRequest({
    appServiceType: SERVICE.GET_DEVICE_INFO_EXTRA,
  });
};

export const useListStateDevices = () => {
  return useSocketRequest({
    appServiceType: SERVICE.LIST_STATE_DEVICES,
  });
};

export const getCameraGroupsByParentId = async (input) => {
  const response = await requestSocketPromiseWithTimeout({
    appServiceType: SERVICE.LIST_DEVICE_GROUP_BY_PARENT_DEVICE_GROUP,
    body: prepareBody(input),
  });

  return parseResponseBody(response);
};

export const useGetCameraGroupsByParentId = (input) => {
  return useSocketQuery(input, {
    appServiceType: SERVICE.LIST_DEVICE_GROUP_BY_PARENT_DEVICE_GROUP,
  });
};

export const getDeviceGroupsByPageV2 = async (input) => {
  const response = await requestSocketPromiseWithTimeout({
    appServiceType: SERVICE.LIST_DEVICE_GROUP_BY_PAGE_V2,
    body: prepareBody(input),
  });

  return parseResponseBody(response);
};

export const useGetDeviceGroupsByPageV2 = () => {
  return useSocketRequest({
    appServiceType: SERVICE.LIST_DEVICE_GROUP_BY_PAGE_V2,
  });
};

export const useListDeviceByDeviceGroupId = () => {
  return useSocketRequest({
    appServiceType: SERVICE.LIST_DEVICE_BY_DEVICE_GROUP_ID,
  });
};

export const useListDeviceByDeviceGroupIdQuery = (input) => {
  return useSocketQuery(input, {
    appServiceType: SERVICE.LIST_DEVICE_BY_DEVICE_GROUP_ID,
  });
};

export const getDeviceToken = async (input) => {
  beeXLogger.debug('🚀 ~ getDeviceToken ~ input:', input);
  const response = await requestSocketPromiseWithTimeout(
    {
      appServiceType: SERVICE.REQUEST_DEVICE_TOKEN,
      body: prepareBody(input),
    },
    {
      timeout: 5000,
    },
  );

  beeXLogger.debug(
    '🚀 ~ getDeviceToken ~ parseResponseBody(response):',
    parseResponseBody(response),
  );
  return parseResponseBody(response);
};
